import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  availableStagesForSeries,
  usePostProcessingOverlay,
} from "hooks/usePostProcessingOverlay";
import { useMemo } from "react";
import { useGetModelPredictionTimeseriesQuery } from "src/store/api/generatedApi";
import { ModelPredictionsChart } from "./ModelPredictionsChart";
import { PostProcessingOverlayPanel } from "./PostProcessingOverlayPanel";

interface ModelPredictionsPanelProps {
  labellingName: string;
  startIso: string;
  endIso: string;
  videoStart: Dayjs;
}

export const ModelPredictionsPanel = ({
  labellingName,
  startIso,
  endIso,
  videoStart,
}: ModelPredictionsPanelProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();

  const { data, isLoading, isError } = useGetModelPredictionTimeseriesQuery({
    tenantName,
    labellingName,
    start: startIso,
    end: endIso,
  });

  const availableStages = useMemo(
    () => availableStagesForSeries(data?.series ?? []),
    [data]
  );
  const { activeStage, selectStage } =
    usePostProcessingOverlay(availableStages);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("unableToLoadModelPredictions")}
        </Typography>
      </Box>
    );
  }

  if (!data || data.series.length === 0) {
    return null;
  }

  const hasPostProcessingStages = availableStages.size > 1;

  return (
    <Box sx={{ overflow: "visible", width: "100%" }}>
      <Stack spacing={0.5}>
        {hasPostProcessingStages && (
          <PostProcessingOverlayPanel
            activeStage={activeStage}
            availableStages={availableStages}
            onSelect={selectStage}
          />
        )}
        <ModelPredictionsChart
          data={data}
          windowStart={videoStart}
          activeStage={hasPostProcessingStages ? activeStage : "raw"}
        />
      </Stack>
    </Box>
  );
};
